// ============================================================================
// AVATAR CATALOG SERVICE
// API service for avatar catalog operations
// ============================================================================

import { api } from './api';
import {
  AvatarDefinition,
  AvatarReviewQueueEntry,
  AvatarGroup,
  BrowseAvatarsParams,
  CreateAvatarInput,
  UpdateAvatarInput,
  AvatarCognitivePolicy
} from '../types';

// ============================================================================
// AVATAR CATALOG CRUD OPERATIONS
// ============================================================================

/**
 * Browse/search avatars with optional filtering
 */
export async function browseAvatars(
  params: BrowseAvatarsParams = {}
): Promise<{ success: boolean; count: number; avatars: AvatarDefinition[] }> {
  const queryParams = new URLSearchParams();

  if (params.scope) queryParams.append('scope', params.scope);
  if (params.ownerId) queryParams.append('ownerId', params.ownerId);
  if (params.category) queryParams.append('category', params.category);
  if (params.isPublic !== undefined) queryParams.append('isPublic', String(params.isPublic));
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.offset) queryParams.append('offset', String(params.offset));

  const response = await api.get(`/api/avatar-catalog/catalog?${queryParams.toString()}`);
  return response.data;
}

/**
 * Get a specific avatar by ID and version
 */
export async function getAvatar(
  avatarDefinitionId: string,
  versionNumber?: number
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const url = versionNumber
    ? `/api/avatar-catalog/catalog/${avatarDefinitionId}?versionNumber=${versionNumber}`
    : `/api/avatar-catalog/catalog/${avatarDefinitionId}`;

  const response = await api.get(url);
  return response.data;
}

/**
 * Create a new avatar
 */
export async function createAvatar(
  input: CreateAvatarInput
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.post('/api/avatar-catalog/catalog', input);
  return response.data;
}

/**
 * Update an existing avatar (creates new version)
 */
export async function updateAvatar(
  avatarDefinitionId: string,
  updates: UpdateAvatarInput,
  changeNotes: string
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.put(`/api/avatar-catalog/catalog/${avatarDefinitionId}`, {
    updates,
    changeNotes
  });
  return response.data;
}

/**
 * Delete an avatar (draft only)
 */
export async function deleteAvatar(
  avatarDefinitionId: string,
  versionNumber: number
): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(
    `/api/avatar-catalog/catalog/${avatarDefinitionId}?versionNumber=${versionNumber}`
  );
  return response.data;
}

/**
 * Clone an existing avatar
 */
export async function cloneAvatar(
  avatarDefinitionId: string,
  versionNumber: number,
  newName: string,
  newOwnerId?: string
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.post(`/api/avatar-catalog/catalog/${avatarDefinitionId}/clone`, {
    versionNumber,
    newName,
    newOwnerId
  });
  return response.data;
}

// ============================================================================
// VERSION MANAGEMENT
// ============================================================================

/**
 * Get all versions of an avatar
 */
export async function getAvatarVersions(
  avatarDefinitionId: string
): Promise<{ success: boolean; versions: AvatarDefinition[] }> {
  const response = await api.get(`/api/avatar-catalog/catalog/${avatarDefinitionId}/versions`);
  return response.data;
}

/**
 * Compare two avatar versions
 */
export async function compareVersions(
  avatarDefinitionId: string,
  version1: number,
  version2: number
): Promise<{ success: boolean; diff: any }> {
  const response = await api.get(
    `/api/avatar-catalog/catalog/${avatarDefinitionId}/compare?v1=${version1}&v2=${version2}`
  );
  return response.data;
}

// ============================================================================
// PUBLISHING WORKFLOW
// ============================================================================

/**
 * Submit avatar for organizational review
 */
export async function submitForReview(
  avatarDefinitionId: string,
  versionNumber: number,
  submittedBy: string,
  organizationId: string,
  submissionNotes?: string
): Promise<{ success: boolean; review: AvatarReviewQueueEntry }> {
  const response = await api.post(`/api/avatar-catalog/catalog/${avatarDefinitionId}/publish`, {
    versionNumber,
    submittedBy,
    organizationId,
    submissionNotes
  });
  return response.data;
}

/**
 * Get pending reviews for an organization
 */
export async function getPendingReviews(
  organizationId: string
): Promise<{ success: boolean; reviews: AvatarReviewQueueEntry[] }> {
  const response = await api.get(
    `/api/avatar-catalog/catalog/pending-review?organizationId=${organizationId}`
  );
  return response.data;
}

/**
 * Get review with full avatar details
 */
export async function getReviewWithAvatar(
  reviewId: string
): Promise<{
  success: boolean;
  review: AvatarReviewQueueEntry;
  avatar: AvatarDefinition;
}> {
  const response = await api.get(`/api/avatar-catalog/catalog/reviews/${reviewId}`);
  return response.data;
}

/**
 * Approve a review (admin only)
 */
export async function approveReview(
  avatarDefinitionId: string,
  reviewId: string,
  reviewedBy: string,
  reviewNotes?: string
): Promise<{
  success: boolean;
  message: string;
  orgAvatarId: string;
}> {
  const response = await api.post(`/api/avatar-catalog/catalog/${avatarDefinitionId}/approve`, {
    reviewId,
    reviewedBy,
    reviewNotes
  });
  return response.data;
}

/**
 * Reject a review (admin only)
 */
export async function rejectReview(
  avatarDefinitionId: string,
  reviewId: string,
  reviewedBy: string,
  rejectionReason: string,
  reviewNotes?: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.post(`/api/avatar-catalog/catalog/${avatarDefinitionId}/reject`, {
    reviewId,
    reviewedBy,
    rejectionReason,
    reviewNotes
  });
  return response.data;
}

/**
 * Withdraw a review (submitter only)
 */
export async function withdrawReview(
  reviewId: string,
  userId: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.post('/api/avatar-catalog/catalog/review/withdraw', {
    reviewId,
    userId
  });
  return response.data;
}

/**
 * Get organization review statistics
 */
export async function getOrgReviewStats(
  organizationId: string
): Promise<{
  success: boolean;
  stats: {
    totalSubmissions: number;
    pending: number;
    approved: number;
    rejected: number;
    averageReviewTime: number;
  };
}> {
  const response = await api.get(`/api/avatar-catalog/catalog/review-stats?organizationId=${organizationId}`);
  return response.data;
}

// ============================================================================
// AVATAR GROUPS
// ============================================================================

/**
 * Get all avatar groups for an organization
 */
export async function getAvatarGroups(
  organizationId: string
): Promise<{ success: boolean; groups: AvatarGroup[] }> {
  const response = await api.get(`/api/avatar-catalog/groups?organizationId=${organizationId}`);
  return response.data;
}

/**
 * Get a specific avatar group
 */
export async function getAvatarGroup(
  groupId: string
): Promise<{ success: boolean; group: AvatarGroup }> {
  const response = await api.get(`/api/avatar-catalog/groups/${groupId}`);
  return response.data;
}

/**
 * Create a new avatar group
 */
export async function createAvatarGroup(
  name: string,
  description: string,
  organizationId: string,
  avatarSlots: Array<{
    slotNumber: number;
    avatarDefinitionId: string;
    versionNumber: number;
  }>,
  createdBy: string
): Promise<{ success: boolean; group: AvatarGroup }> {
  const response = await api.post('/api/avatar-catalog/groups', {
    name,
    description,
    organizationId,
    avatarSlots,
    createdBy
  });
  return response.data;
}

/**
 * Update an avatar group
 */
export async function updateAvatarGroup(
  groupId: string,
  updates: {
    name?: string;
    description?: string;
    avatarSlots?: Array<{
      slotNumber: number;
      avatarDefinitionId: string;
      versionNumber: number;
    }>;
  }
): Promise<{ success: boolean; group: AvatarGroup }> {
  const response = await api.put(`/api/avatar-catalog/groups/${groupId}`, updates);
  return response.data;
}

/**
 * Delete an avatar group
 */
export async function deleteAvatarGroup(
  groupId: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(`/api/avatar-catalog/groups/${groupId}`);
  return response.data;
}

// ============================================================================
// EXPORT & DOCUMENTATION
// ============================================================================

/**
 * Export avatar as Agent SOP markdown
 */
export async function exportAsAgentSOP(
  avatarDefinitionId: string,
  versionNumber?: number
): Promise<{ success: boolean; markdown: string }> {
  const url = versionNumber
    ? `/api/avatar-catalog/catalog/${avatarDefinitionId}/export-sop?versionNumber=${versionNumber}`
    : `/api/avatar-catalog/catalog/${avatarDefinitionId}/export-sop`;

  const response = await api.get(url);
  return response.data;
}

// ============================================================================
// PROJECT AVATAR OPERATIONS
// ============================================================================

/**
 * Get all project-scoped avatars for a project
 */
export async function getProjectAvatars(
  projectId: string
): Promise<{ success: boolean; count: number; avatars: AvatarDefinition[] }> {
  const response = await api.get(`/api/projects/${projectId}/avatars`);
  return response.data;
}

export interface AvatarPerformanceStat {
  total: number;
  accepted: number;
  rejected: number;
  acceptanceRate: number;
  sessions: number;
  builtUponRate: number;
  builtUpon?: number;
}

export async function getProjectAvatarPerformance(
  projectId: string
): Promise<Record<string, AvatarPerformanceStat>> {
  const response = await api.get(`/api/projects/${projectId}/avatars/performance`);
  return response.data.performance ?? {};
}

export interface CognitionGraphNode {
  id: string;
  type: string;
  labels: string[];
  name: string;
  text?: string;
  timesLedToPositive?: number;
  timesLedToNegative?: number;
  [key: string]: any;
}

export interface CognitionGraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  [key: string]: any;
}

export interface CognitionGraphData {
  nodes: CognitionGraphNode[];
  edges: CognitionGraphEdge[];
  nodeCount: number;
  edgeCount: number;
}

/**
 * The avatar's cognitive policy graph (Goal/Lens/Question/EvidenceRule/
 * DecisionCriterion + activation-outcome counters) for the graph view. Works
 * for both org-wide and project-scoped avatars — this is avatar-scoped, not
 * project-scoped.
 */
export async function getAvatarCognitionGraph(avatarDefinitionId: string): Promise<CognitionGraphData> {
  const response = await api.get(`/api/avatar-catalog/catalog/${avatarDefinitionId}/cognition-graph`);
  return response.data;
}

/**
 * Create a new project-scoped avatar
 */
export async function createProjectAvatar(
  projectId: string,
  input: {
    name: string;
    description: string;
    systemPrompt: string;
    keywords: string[];
    category?: string;
    ttsProvider?: string;
    ttsVoiceId?: string;
    imageUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
  }
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.post(`/api/projects/${projectId}/avatars`, input);
  return response.data;
}

/**
 * Update a project-scoped avatar definition
 */
export async function updateProjectAvatar(
  projectId: string,
  avatarId: string,
  input: {
    name?: string;
    description?: string;
    systemPrompt?: string;
    keywords?: string[];
    category?: string;
    ttsProvider?: string;
    ttsVoiceId?: string;
    imageUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    isEnabled?: boolean;
    cognitivePolicy?: AvatarCognitivePolicy;
  }
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.put(`/api/projects/${projectId}/avatars/${avatarId}`, input);
  return response.data;
}

/**
 * Create a project avatar from an existing session persona (AI-generates the prompt)
 */
export async function createProjectAvatarFromPersona(
  projectId: string,
  personaId: string,
  sessionId: string
): Promise<{ success: boolean; avatar: AvatarDefinition; personaId: string }> {
  const response = await api.post(`/api/projects/${projectId}/avatars/from-persona`, {
    personaId,
    sessionId
  });
  return response.data;
}

/**
 * Instantiate a project-scoped avatar definition into a live session
 */
export async function instantiateAvatarInSession(
  sessionId: string,
  avatarDefinitionId: string
): Promise<{ success: boolean; avatar: { id: string; name: string; role: string; avatarDefinitionId: string } }> {
  const response = await api.post(`/api/sessions/${sessionId}/avatars/instantiate`, {
    avatarDefinitionId
  });
  return response.data;
}

/**
 * Enable or disable a project avatar (soft toggle, does not delete)
 */
export async function setProjectAvatarEnabled(
  projectId: string,
  avatarId: string,
  isEnabled: boolean
): Promise<{ success: boolean; avatar: AvatarDefinition }> {
  const response = await api.patch(`/api/projects/${projectId}/avatars/${avatarId}/enabled`, { isEnabled });
  return response.data;
}

/**
 * Sync all enabled project avatars into a running session (adds any missing ones)
 */
export async function syncProjectAvatarsIntoSession(
  sessionId: string
): Promise<{ success: boolean; added: { id: string; name: string; role: string; avatarDefinitionId: string }[] }> {
  const response = await api.post(`/api/sessions/${sessionId}/project-avatars/sync`, {});
  return response.data;
}

/**
 * AI-generate a systemPrompt and keywords from a name + description
 */
export async function generateAvatarSystemPrompt(
  projectId: string,
  name: string,
  description: string,
  goals?: string[],
  painPoints?: string[]
): Promise<{ success: boolean; systemPrompt: string; keywords: string[] }> {
  const response = await api.post(`/api/projects/${projectId}/avatars/generate-prompt`, {
    name,
    description,
    goals,
    painPoints
  });
  return response.data;
}

/**
 * Generate a sketchbook portrait image for an avatar via n8n image gen workflow.
 * Creates a project task, fires n8n, then polls the task until completed.
 */
export async function generateAvatarImage(
  projectId: string,
  avatarId: string,
  name: string,
  description: string
): Promise<{ success: boolean; imageUrl: string; imageS3Key: string; taskId: string }> {
  const { data } = await api.post(`/api/projects/${projectId}/avatars/generate-image`, { avatarId, name, description });
  const { taskId } = data;

  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 5000));
    const { data: taskData } = await api.get(`/api/projects/${projectId}/tasks/${taskId}`);
    const task = taskData.task;
    if (task.status === 'completed') {
      const output = task.outputs?.find((o: any) => o.type === 'avatar_portrait');
      const imageUrl = output?.content?.imageUrl;
      const imageS3Key = output?.content?.s3Key;
      if (!imageUrl) throw new Error('Task completed but no image URL in outputs');
      return { success: true, imageUrl, imageS3Key: imageS3Key || imageUrl, taskId };
    }
    if (task.status === 'failed') {
      const errStep = task.stepResults?.find((s: any) => s.error);
      throw new Error(errStep?.error || 'Image generation failed');
    }
  }

  throw new Error('Image generation timed out');
}

/**
 * Generate a sketchbook portrait image for a general catalog avatar (SESSION/
 * USER/ORGANIZATION scope). Same shape as generateAvatarImage, but tracked via
 * the catalog's own avatarDefinitionId-keyed task endpoint rather than a
 * project task, since catalog avatars have no projectId.
 */
export async function generateCatalogAvatarImage(
  avatarDefinitionId: string,
  name: string,
  description: string
): Promise<{ success: boolean; imageUrl: string; imageS3Key: string; taskId: string }> {
  const { data } = await api.post(`/api/avatar-catalog/catalog/${avatarDefinitionId}/generate-image`, { name, description });
  const { taskId } = data;

  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 5000));
    const { data: taskData } = await api.get(`/api/avatar-catalog/catalog/${avatarDefinitionId}/image-task/${taskId}`);
    if (taskData.status === 'completed') {
      if (!taskData.imageUrl) throw new Error('Task completed but no image URL in outputs');
      return { success: true, imageUrl: taskData.imageUrl, imageS3Key: taskData.s3Key || taskData.imageUrl, taskId };
    }
    if (taskData.status === 'failed') {
      throw new Error(taskData.error || 'Image generation failed');
    }
  }

  throw new Error('Image generation timed out');
}

/**
 * Accept a generated image for a catalog avatar — updates imageUrl in place,
 * no new version created.
 */
export async function setCatalogAvatarImage(
  avatarDefinitionId: string,
  imageUrl: string
): Promise<{ success: boolean }> {
  const response = await api.patch(`/api/avatar-catalog/catalog/${avatarDefinitionId}/image`, { imageUrl });
  return response.data;
}

/**
 * Download avatar SOP as file
 */
export async function downloadAvatarSOP(
  avatarDefinitionId: string,
  avatarName: string,
  versionNumber?: number
): Promise<void> {
  const result = await exportAsAgentSOP(avatarDefinitionId, versionNumber);

  // Create blob and trigger download
  const blob = new Blob([result.markdown], { type: 'text/markdown' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${avatarName.replace(/\s+/g, '-').toLowerCase()}-sop.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
